import {
	useSingleSelectField,
	type SingleSelectFieldValue,
} from '@atlassian/jira-issue-field-select';
import { SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
	useGenericClipboardHandler,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import { singleSelectSupportedTypes } from '../supported-field-types';
import type { TransformedSingleSelect } from '../types';

const isTransformedSingleSelectOption = (
	fieldValue: unknown,
): fieldValue is TransformedSingleSelect =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'value' in fieldValue &&
	'optionId' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): SingleSelectFieldValue | null => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, singleSelectSupportedTypes)
	) {
		return null;
	}

	if (!isTransformedSingleSelectOption(metadata.fieldValue)) {
		return null;
	}

	return {
		id: metadata.fieldValue.optionId,
		value: metadata.fieldValue.value,
	};
};

const useSingleSelectClipboardHandler: PasteHandler = (
	issue: { key: string },
	field: ListIssueFieldTransformed,
) => {
	const [, { saveValue }] = useSingleSelectField<SingleSelectFieldValue>({
		fieldKey: field.fieldId,
		issueKey: issue.key,
	});

	return useGenericClipboardHandler(field, getPastedValue, saveValue);
};

const SingleSelectClipboardHelper = createClipboardHelperComponent(useSingleSelectClipboardHandler);

export const singleSelectClipboardHandler = {
	[SELECT_CF_TYPE]: SingleSelectClipboardHelper,
};
